/**
 * Map PostAJob flow answers → POST /notices/collaboration payload.
 * Missing required fields use demo values until AI enrichment (TP-302).
 */

const DEMO_ROLE = 'Graphic Designer';
const DEMO_TITLE = 'New project';
const DEMO_SPEC = 'Project details to follow.';
const DEFAULT_CURRENCY = 'usd';
const DEFAULT_EXPIRES_IN_DAYS = 14;

const CAPACITY_TO_HIRING = {
  personal_project: 'As an individual for a personal project',
  my_company: 'On behalf of my company',
  client_or_customer: 'On behalf of another company',
};

const EXPERIENCE_LABELS = {
  beginner: 'Beginner: $15-25',
  junior: 'Junior: $25-35',
  mid_level: 'Mid-level (Industry Average): $35-75',
  senior: 'Senior: $75-100',
  expert: 'Expert: $100+',
};

/** Remap PostAJob duration keys → Twine priority question strings (n8n / EnumMapper). */
const DURATION_TO_PRIORITY = {
  a_few_hours: 'A few hours',
  a_day_or_two: 'A day or two',
  up_to_a_week: 'Best part of a week',
  more_than_a_week: 'More than a week',
  long_term_ongoing: 'Long term',
  not_sure: 'More than a week',
};

/** Top of budget range presets in USD (n8n GPT: top of range). */
const BUDGET_RANGE_USD_TOP = {
  micro: 30,
  simple: 250,
  very_small: 750,
  small: 1500,
  medium: 3000,
  large: 5000,
  larger: 10000,
  very_large: 20000,
  complex: 50000,
  huge: 100000,
  major: 100000,
};

/** Midpoint USD for set_a_budget range fallbacks (sent as cents). */
const BUDGET_RANGE_USD = {
  micro: 20,
  simple: 140,
  very_small: 500,
  small: 1125,
  medium: 2250,
  large: 4000,
  larger: 7500,
  very_large: 15000,
  complex: 35000,
  huge: 75000,
  major: 100000,
};

/** n8n Post job: amounts under $500 post as negotiable (-1). */
const MIN_POSTED_BUDGET_CENTS = 50000;

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * @param {number} amountCents
 * @returns {number}
 */
const applyMinPostedBudget = (amountCents) =>
  amountCents > 0 && amountCents < MIN_POSTED_BUDGET_CENTS ? -1 : amountCents;

const RANGE_SPLIT = /\s*(?:–|-|\bto\b)\s*/i;

/**
 * @param {number} amount
 * @returns {boolean}
 */
const isPostableBudgetAmount = (amount) => {
  if (!Number.isFinite(amount) || amount <= 0) {
    return false;
  }
  const cents = Math.round(amount * 100);
  return Number.isSafeInteger(cents) && cents > 0;
};
const CURRENCY_PREFIXES = [
  ['gbp', /^(?:£|gbp)\s*/i],
  ['usd', /^(?:\$|usd)\s*/i],
  ['eur', /^(?:€|eur)\s*/i],
];

/**
 * @param {string} token
 * @returns {{ amount: number, currency: string|null }|null}
 */
const parseSingleBudgetDollars = (token) => {
  let value = String(token || '').trim();
  let currency = null;
  for (let i = 0; i < CURRENCY_PREFIXES.length; i += 1) {
    const code = CURRENCY_PREFIXES[i][0];
    const pattern = CURRENCY_PREFIXES[i][1];
    if (pattern.test(value)) {
      currency = code;
      value = value.replace(pattern, '').trim();
      break;
    }
  }

  const match = value.match(/^(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?\s*([km])?$/i);
  if (!match) {
    return null;
  }

  let amount = parseFloat(`${match[1]}${match[2] || ''}`.replace(/,/g, ''));
  const suffix = (match[3] || '').toLowerCase();
  if (suffix === 'k') {
    amount *= 1000;
  } else if (suffix === 'm') {
    amount *= 1000000;
  }

  return { amount, currency };
};

/**
 * @param {Date} date
 * @returns {string} Y-m-d H:i:s
 */
export const formatExpiresAt = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(
    date.getSeconds()
  )}`;

/**
 * @param {number} [daysFromNow=14]
 * @param {Date} [now]
 * @returns {string}
 */
export const defaultExpiresAt = (
  daysFromNow = DEFAULT_EXPIRES_IN_DAYS,
  now = new Date()
) => {
  const expires = new Date(now.getTime());
  expires.setDate(expires.getDate() + daysFromNow);
  expires.setHours(23, 59, 59, 0);
  return formatExpiresAt(expires);
};

/**
 * Parse a set_a_budget amount string (parity with PostAJobFieldMapper).
 * Leading £ → gbp, $ → usd, € → eur; otherwise usd.
 *
 * @param {string} amountRaw
 * @returns {{ amountCents: number, currency: string }|null}
 */
export const parseBudgetAmount = (amountRaw) => {
  // Unicode-aware trim so leading NBSP etc. don't hide £/$/€.
  const trimmed = String(amountRaw || '').replace(/^\s+|\s+$/gu, '');
  if (!trimmed) {
    return null;
  }

  const parts = trimmed.split(RANGE_SPLIT);
  if (parts.length < 1 || parts.length > 2) {
    return null;
  }

  const parsed = parts.map((part) => parseSingleBudgetDollars(part));
  if (
    parsed.some(
      (token) => token == null || !isPostableBudgetAmount(token.amount)
    )
  ) {
    return null;
  }
  if (parsed.length === 2 && parsed[0].amount > parsed[1].amount) {
    return null;
  }

  let currency = null;
  for (let i = 0; i < parsed.length; i += 1) {
    if (parsed[i].currency == null) {
      continue;
    }
    if (currency != null && currency !== parsed[i].currency) {
      return null;
    }
    currency = parsed[i].currency;
  }

  return {
    amountCents: Math.round(parsed[parsed.length - 1].amount * 100),
    currency: currency || DEFAULT_CURRENCY,
  };
};

/**
 * @param {Object} answers
 * @returns {{ amount: number, currency: string }}
 */
export const resolveBudget = (answers = {}) => {
  const budgetType = answers.budgetType;

  if (budgetType === 'receive_offers') {
    const range = answers.budgetRange;
    if (
      range &&
      range !== 'prefer_not_to_say' &&
      BUDGET_RANGE_USD_TOP[range] != null
    ) {
      return {
        amount: applyMinPostedBudget(BUDGET_RANGE_USD_TOP[range] * 100),
        currency: DEFAULT_CURRENCY,
      };
    }
    return { amount: -1, currency: DEFAULT_CURRENCY };
  }

  if (budgetType === 'set_a_budget') {
    const amountRaw = String(answers.budgetAmount || '').replace(
      /^\s+|\s+$/gu,
      ''
    );
    if (amountRaw) {
      const parsed = parseBudgetAmount(amountRaw);
      if (parsed) {
        return {
          amount: applyMinPostedBudget(parsed.amountCents),
          currency: parsed.currency,
        };
      }
      return { amount: -1, currency: DEFAULT_CURRENCY };
    }

    const range = answers.budgetRange;
    if (range === 'prefer_not_to_say' || !range) {
      return { amount: -1, currency: DEFAULT_CURRENCY };
    }
    if (BUDGET_RANGE_USD[range] != null) {
      return {
        amount: applyMinPostedBudget(BUDGET_RANGE_USD[range] * 100),
        currency: DEFAULT_CURRENCY,
      };
    }
  }

  return { amount: -1, currency: DEFAULT_CURRENCY };
};

const WORK_LOCATION_KEYS = {
  fully_remote: 'fully_remote',
  fullyremote: 'fully_remote',
  'fully remote': 'fully_remote',
  remote_within_country: 'remote_within_country',
  remote_within_a_country: 'remote_within_country',
  remotewithinacountry: 'remote_within_country',
  'remote within a country': 'remote_within_country',
  hybrid: 'hybrid',
  on_site: 'on_site',
  onsite: 'on_site',
  'on site': 'on_site',
  'on-site': 'on_site',
};

/**
 * @param {string} [workLocation]
 * @returns {string}
 */
export const normaliseWorkLocation = (workLocation) => {
  // n8n matches Fillout label "Fully remote"; collapse unicode whitespace too.
  const trimmed = String(workLocation || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!trimmed) {
    return '';
  }
  if (WORK_LOCATION_KEYS[trimmed]) {
    return WORK_LOCATION_KEYS[trimmed];
  }
  const compact = trimmed.replace(/[\s-]+/g, '_');
  if (WORK_LOCATION_KEYS[compact]) {
    return WORK_LOCATION_KEYS[compact];
  }
  const alpha = trimmed.replace(/[^a-z0-9]+/g, '');
  return WORK_LOCATION_KEYS[alpha] || WORK_LOCATION_KEYS[compact] || compact;
};

/**
 * @param {Object} answers
 * @returns {{ remote: number, location_text: string|null }}
 */
export const resolveLocation = (answers = {}) => {
  const workLocation = normaliseWorkLocation(
    answers.workLocation || answers.work_location
  );
  const country = String(
    answers.locationCountry || answers.country || ''
  ).trim();
  const city = String(answers.city || '').trim();
  const location_text =
    city && country ? `${city}, ${country}` : country || city;

  // n8n: remoteAnswer === 'Fully remote' ? 1 : 0; no location for fully remote
  if (workLocation === 'fully_remote') {
    return { remote: 1, location_text: null };
  }

  if (workLocation === 'remote_within_country') {
    return { remote: 0, location_text };
  }

  if (!workLocation && !country && !city) {
    return { remote: 1, location_text: null };
  }

  return { remote: 0, location_text };
};

/**
 * @param {Object} answers
 * @returns {string}
 */
export const buildSpec = (answers = {}) => {
  const parts = [];
  const description = String(answers.description || '').trim();
  const examplesText = String(answers.examplesText || '').trim();
  const about = String(answers.about || '').trim();
  const companyName = String(answers.companyName || '').trim();
  const deadline = String(answers.deadline || '').trim();
  const skills = String(answers.skills || '').trim();
  const categories = Array.isArray(answers.categories)
    ? answers.categories.filter(Boolean)
    : [];
  const categoryOther = String(answers.categoryOther || '').trim();

  if (description) {
    parts.push(description);
  }
  if (examplesText) {
    parts.push(`Examples / references:\n${examplesText}`);
  }
  if (about) {
    parts.push(`About the client:\n${about}`);
  }
  if (companyName) {
    parts.push(`Company: ${companyName}`);
  }
  if (deadline) {
    parts.push(`Deadline: ${deadline}`);
  }
  if (skills) {
    parts.push(`Skills: ${skills}`);
  }
  if (categories.length || categoryOther) {
    const labels = [...categories];
    if (categoryOther) {
      labels.push(categoryOther);
    }
    parts.push(`Categories: ${labels.join(', ')}`);
  }

  const spec = parts.join('\n\n').trim();
  return spec || DEMO_SPEC;
};

/**
 * @param {Object} answers
 * @returns {string}
 */
export const resolveHiringCapacity = (answers = {}) =>
  CAPACITY_TO_HIRING[answers.capacity] || CAPACITY_TO_HIRING.personal_project;

/**
 * @param {Object} answers
 * @returns {string[]}
 */
export const resolveExperienceLevels = (answers = {}) => {
  const levels = Array.isArray(answers.experienceLevels)
    ? answers.experienceLevels
    : [];
  const mapped = levels
    .map((value) => EXPERIENCE_LABELS[value] || value)
    .filter(Boolean);
  return mapped.length > 0 ? mapped : [EXPERIENCE_LABELS.mid_level];
};

/**
 * Remapped duration for StoreRequest → questions (setPriority).
 * Missing when the step was skipped (e.g. contract types).
 *
 * @param {Object} answers
 * @returns {string|null}
 */
export const resolveDuration = (answers = {}) => {
  const type = answers.type;
  if (type === 'part_time_contract' || type === 'full_time_contract') {
    return null;
  }
  const key = answers.duration;
  if (!key || !DURATION_TO_PRIORITY[key]) {
    return null;
  }
  return DURATION_TO_PRIORITY[key];
};

/**
 * @param {Object} answers
 * @returns {Object} createBrief payload
 */
export const buildJobPayload = (answers = {}) => {
  const title = String(answers.title || '').trim() || DEMO_TITLE;
  const role = String(answers.role || '').trim() || DEMO_ROLE;
  const { amount, currency } = resolveBudget(answers);
  const { remote, location_text } = resolveLocation(answers);
  const duration = resolveDuration(answers);

  const payload = {
    text: title.slice(0, 140),
    spec: buildSpec(answers).slice(0, 10000),
    role,
    remote,
    location_text,
    amount,
    currency,
    expires_at: defaultExpiresAt(),
    hiring_capacity: resolveHiringCapacity(answers),
    experience_levels: resolveExperienceLevels(answers),
  };

  if (duration) {
    payload.duration = duration;
  }

  return payload;
};
